import { useEffect, useState } from "react";
import { Play, ExternalLink, Instagram, Facebook, Twitch, Youtube, Music2, Radio } from "lucide-react";
import { fetchShow } from "../lib/api";
import { SOCIALS, STUDIO_SHOT, LOGO_PRIMARY } from "../lib/brand";
import SEO from "../components/SEO";

const ICONS = { Instagram, Facebook, Twitch, Youtube, Music2, Radio };

const PLATFORMS = [
  { key: "spotify", label: "Spotify", desc: "Stream full episodes", url: "https://open.spotify.com/show/unfilteredandonball" },
  { key: "youtube", label: "YouTube", desc: "Watch the full show + clips", url: "https://youtube.com/@Unfilteredandonball" },
  { key: "tiktok", label: "TikTok", desc: "Hot takes, short form", url: "https://tiktok.com/@Unfilteredandonball" },
  { key: "twitch", label: "Twitch", desc: "Watch us record live", url: "https://twitch.tv/Unfilteredandonball" },
  { key: "instagram", label: "Instagram", desc: "Behind the scenes", url: "https://instagram.com/Unfilteredandonball" },
  { key: "facebook", label: "Facebook", desc: "Community + live drops", url: "https://facebook.com/Unfilteredandonball" },
];

export default function Listen() {
  const [show, setShow] = useState(null);
  useEffect(() => { fetchShow().then(setShow).catch(() => {}); }, []);

  return (
    <div data-testid="listen-page">
      <SEO
        title="Listen · Spotify, YouTube, Apple, Twitch"
        description="New drop every Monday. Find Unfiltered & On-Ball on Spotify, YouTube, TikTok, Twitch, Instagram & Facebook — pick your platform."
        image={STUDIO_SHOT}
      />
      <section className="border-b-2 border-[var(--ink)]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-14 md:py-20 grid md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-3">
            <div className="text-kicker">Tune In · Subscribe · Follow</div>
            <h1 className="text-headline text-5xl md:text-7xl lg:text-8xl mt-2">
              Listen.<br />
              <span className="text-[var(--rust)]">Watch.</span><br />
              Argue Back.
            </h1>
            <p className="text-dek text-xl md:text-2xl mt-5 max-w-xl">
              New drop every Monday. Find us on every platform that matters — pick your poison below.
            </p>
          </div>
          <div className="md:col-span-2">
            <img src={STUDIO_SHOT} alt="Studio" loading="eager"
                 className="w-full aspect-square object-cover border-2 border-[var(--ink)] shadow-[8px_8px_0_var(--rust)]" />
          </div>
        </div>
      </section>

      <section className="py-14 border-b-2 border-[var(--ink)]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between mb-8 pb-4 border-b-2 border-[var(--ink)]">
            <h2 className="text-headline text-3xl md:text-5xl">Pick Your Platform</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PLATFORMS.map((p, i) => {
              const soc = SOCIALS.find((s) => s.key === p.key);
              const Ic = soc && ICONS[soc.icon] ? ICONS[soc.icon] : Music2;
              return (
                <a key={p.key} href={p.url} target="_blank" rel="noreferrer"
                   className="sticker-card p-7 flex flex-col justify-between min-h-[200px]"
                   data-testid={`listen-platform-${p.key}`}>
                  <div>
                    <div className="flex items-center gap-3 text-kicker">
                      <Ic size={16} /> {p.label}
                    </div>
                    <div className="text-headline text-3xl mt-4">{p.label}</div>
                    <p className="text-dek mt-2 text-sm">{p.desc}</p>
                  </div>
                  <div className="mt-6 inline-flex items-center gap-2 font-display uppercase tracking-widest text-sm text-[var(--rust)]">
                    Open <ExternalLink size={14} />
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick play row */}
      <section className="py-16 bg-[var(--ink)] text-[var(--paper)] relative overflow-hidden noise-overlay">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 text-center relative">
          <img src={LOGO_PRIMARY} alt="Unfiltered & On-Ball" loading="lazy"
               className="w-40 md:w-56 mx-auto mb-6" />
          <div className="text-kicker text-[var(--rust)]">The Latest Episode</div>
          <h2 className="text-headline text-4xl md:text-6xl mt-2 mb-6">
            Monday Morning <span className="text-[var(--rust)]">Drop</span>
          </h2>
          <p className="font-serif-ed text-lg md:text-xl max-w-2xl mx-auto text-[var(--paper-deep)]/85 mb-8">
            The week that cracked the playoff picture open — plus the coach that's about to get fired and the rookie class everyone slept on.
          </p>
          <a href="https://open.spotify.com/show/unfilteredandonball" target="_blank" rel="noreferrer"
             className="btn-rust inline-flex" data-testid="listen-play-latest">
            <Play size={16} fill="currentColor" /> Play Latest Episode
          </a>
        </div>
      </section>
    </div>
  );
}
